import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import MediaModal from '../components/MediaModal';
import { fetchMovieDetails, fetchTrendingMovies, fetchTrendingSeries } from '../services/tmdb';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark as solidBookmark } from '@fortawesome/free-solid-svg-icons';
import { faBookmark as regularBookmark } from '@fortawesome/free-regular-svg-icons';

// ========== TRANSLATIONS ==========
const translations = {
  en: {
    trendingMovies: 'TRENDING MOVIES THIS WEEK',
    trendingSeries: 'TRENDING SERIES THIS WEEK',
    loadingTrendingMovies: 'Loading trending movies...',
    loadingTrendingSeries: 'Loading trending series...',
  },
  fr: {
    trendingMovies: 'FILMS TENDANCES CETTE SEMAINE',
    trendingSeries: 'SÉRIES TENDANCES CETTE SEMAINE',
    loadingTrendingMovies: 'Chargement des films tendances...',
    loadingTrendingSeries: 'Chargement des séries tendances...',
  },
  es: {
    trendingMovies: 'PELÍCULAS TENDENCIA ESTA SEMANA',
    trendingSeries: 'SERIES TENDENCIA ESTA SEMANA',
    loadingTrendingMovies: 'Cargando películas tendencia...',
    loadingTrendingSeries: 'Cargando series tendencia...',
  },
  it: {
    trendingMovies: 'FILM DI TENDENZA QUESTA SETTIMANA',
    trendingSeries: 'SERIE DI TENDENZA QUESTA SETTIMANA',
    loadingTrendingMovies: 'Caricamento film di tendenza...',
    loadingTrendingSeries: 'Caricamento serie di tendenza...',
  },
  nl: {
    trendingMovies: 'TRENDING FILMS DEZE WEEK',
    trendingSeries: 'TRENDING SERIES DEZE WEEK',
    loadingTrendingMovies: 'Trending films laden...',
    loadingTrendingSeries: 'Trending series laden...',
  },
};

const TrendingPage = () => {
  const [trendingMovies, setTrendingMovies] = useState([]);
  const [trendingSeries, setTrendingSeries] = useState([]);
  const [moviesLoading, setMoviesLoading] = useState(true);
  const [seriesLoading, setSeriesLoading] = useState(true);

  // Unified modal state
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaType, setSelectedMediaType] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // Refs for carousel containers
  const moviesCarouselRef = useRef(null);
  const seriesCarouselRef = useRef(null);

  // Index state for each carousel
  const [moviesIndex, setMoviesIndex] = useState(0);
  const [seriesIndex, setSeriesIndex] = useState(0);

  // ===== Responsive layout state =====
  const [itemsPerView, setItemsPerView] = useState(6);
  const [gap, setGap] = useState(80);

  useEffect(() => {
    const updateLayout = () => {
      const width = window.innerWidth;
      let items, gapSize;
      if (width <= 768) {
        items = 2;
        gapSize = 27;
      } else if (width <= 1024) {
        items = 4;
        gapSize = 60;
      } else if (width <= 1200) {
        items = 5;
        gapSize = 70;
      } else {
        items = 6;
        gapSize = 80;
      }
      setItemsPerView(items);
      setGap(gapSize);
    };
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  // ===== Mobile detection for season abbreviation =====
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ===== Scroll position ref for modal restore =====
  const scrollPositionRef = useRef(0);

  // Maps to store extra details (runtime for movies, season count for series)
  const [movieDetailsMap, setMovieDetailsMap] = useState({});
  const [seriesDetailsMap, setSeriesDetailsMap] = useState({});

  // ===== Watchlist state =====
  const [watchlist, setWatchlist] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('watchlist')) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('watchlist', JSON.stringify(watchlist));
  }, [watchlist]);

  // Language
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });

  const t = (key) => {
    const langData = translations[lang] || translations.en;
    return langData[key] || key;
  };

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredLanguage') {
        setLang(e.newValue || 'en');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // ===== Fetch trending data =====
  const fetchTrendingData = useCallback(async () => {
    setMoviesLoading(true);
    setSeriesLoading(true);

    try {
      const moviesResponse = await fetchTrendingMovies();
      const seriesResponse = await fetchTrendingSeries();

      const filteredMovies = moviesResponse.results
        .filter(movie => movie.vote_count >= 30)
        .slice(0, 15);

      const filteredSeries = seriesResponse.results
        .filter(series => series.vote_count >= 30)
        .slice(0, 15);

      setTrendingMovies(filteredMovies);
      setTrendingSeries(filteredSeries);

      // Fetch details for each movie (runtime)
      const movieDetailsPromises = filteredMovies.map(movie =>
        fetchMovieDetails(movie.id).catch(() => null)
      );
      const movieDetailsResults = await Promise.all(movieDetailsPromises);
      const movieMap = {};
      movieDetailsResults.forEach((details, index) => {
        if (details) {
          movieMap[filteredMovies[index].id] = details;
        }
      });
      setMovieDetailsMap(movieMap);

      // Fetch details for each series (season count and years)
      const seriesDetailsPromises = filteredSeries.map(series =>
        fetch(`https://api.themoviedb.org/3/tv/${series.id}?api_key=57240db50c2008e78c261e1a934627e4&language=en-US`)
          .then(res => res.json())
          .catch(() => null)
      );
      const seriesDetailsResults = await Promise.all(seriesDetailsPromises);
      const seriesMap = {};
      seriesDetailsResults.forEach((details, index) => {
        if (details) {
          seriesMap[filteredSeries[index].id] = details;
        }
      });
      setSeriesDetailsMap(seriesMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setMoviesLoading(false);
      setSeriesLoading(false);
    }
  }, []);

  // ---- Modal functions ----
  const openMediaModal = (media, mediaType) => {
    scrollPositionRef.current = window.scrollY;
    setSelectedMedia(media);
    setSelectedMediaType(mediaType);
    const newSearchParams = new URLSearchParams(searchParams);
    if (mediaType === 'movie') {
      newSearchParams.set('movie', media.id);
      newSearchParams.delete('series');
    } else {
      newSearchParams.set('series', media.id);
      newSearchParams.delete('movie');
    }
    setSearchParams(newSearchParams);
  };

  const openMovieModal = async (movieId) => {
    scrollPositionRef.current = window.scrollY;
    try {
      const detailedMovie = await fetchMovieDetails(movieId);
      openMediaModal(detailedMovie, 'movie');
    } catch (error) {
      console.error('Error fetching movie details:', error);
      const movieFromList = trendingMovies.find(m => m.id === movieId);
      if (movieFromList) {
        openMediaModal(movieFromList, 'movie');
      } else {
        closeModal();
      }
    }
  };

  const openSeriesModal = async (seriesId) => {
    scrollPositionRef.current = window.scrollY;
    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/tv/${seriesId}?api_key=57240db50c2008e78c261e1a934627e4&language=en-US`
      );
      const detailedSeries = await response.json();
      openMediaModal(detailedSeries, 'tv');
    } catch (error) {
      console.error('Error fetching series details:', error);
      const seriesFromList = trendingSeries.find(s => s.id === seriesId);
      if (seriesFromList) {
        openMediaModal(seriesFromList, 'tv');
      } else {
        closeModal();
      }
    }
  };

  const handleMovieClick = async (movie) => {
    openMovieModal(movie.id);
  };

  const handleSeriesClick = async (series) => {
    openSeriesModal(series.id);
  };

  const handleMediaChange = (media, mediaType) => {
    setSelectedMedia(media);
    setSelectedMediaType(mediaType);
    const newSearchParams = new URLSearchParams(searchParams);
    if (mediaType === 'movie') {
      newSearchParams.set('movie', media.id);
      newSearchParams.delete('series');
    } else {
      newSearchParams.set('series', media.id);
      newSearchParams.delete('movie');
    }
    setSearchParams(newSearchParams);
  };

  const closeModal = () => {
    setSelectedMedia(null);
    setSelectedMediaType(null);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('movie');
    newSearchParams.delete('series');
    setSearchParams(newSearchParams);

    if (scrollPositionRef.current > 0) {
      window.scrollTo({ top: scrollPositionRef.current, behavior: 'auto' });
    }
  };

  // ===== URL parameter handling on load =====
  useEffect(() => {
    const movieId = searchParams.get('movie');
    const seriesId = searchParams.get('series');

    if (movieId && !isNaN(parseInt(movieId))) {
      openMovieModal(parseInt(movieId));
    } else if (seriesId && !isNaN(parseInt(seriesId))) {
      openSeriesModal(parseInt(seriesId));
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchTrendingData();
  }, [fetchTrendingData]);

  // ===== Watchlist helpers =====
  const isInWatchlist = (id, type) => {
    return watchlist.some(item => item.id === id && item.type === type);
  };

  const toggleWatchlist = (e, item, type) => {
    e.stopPropagation();
    const existingIndex = watchlist.findIndex(w => w.id === item.id && w.type === type);
    if (existingIndex !== -1) {
      const newWatchlist = [...watchlist.slice(0, existingIndex), ...watchlist.slice(existingIndex + 1)];
      setWatchlist(newWatchlist);
    } else {
      const newItem = {
        id: item.id,
        title: item.title || item.name,
        poster_path: item.poster_path,
        release_date: item.release_date || item.first_air_date,
        vote_average: item.vote_average,
        type: type,
      };
      setWatchlist([...watchlist, newItem]);
    }
  };

  // ===== Scroll carousel =====
  const scrollCarousel = (trackRef, direction, totalItems, currentIndex, setIndex) => {
    if (!trackRef.current) return;
    const track = trackRef.current;
    const firstCard = track.querySelector('.trending-card');
    if (!firstCard) return;
    const cardWidth = firstCard.offsetWidth;
    const actualGap = parseFloat(getComputedStyle(track).gap) || gap;
    const step = cardWidth + actualGap;

    const maxIndex = Math.max(0, totalItems - itemsPerView);
    let newIndex = currentIndex;
    if (direction === 'left') {
      newIndex = Math.max(0, currentIndex - 1);
    } else {
      newIndex = Math.min(maxIndex, currentIndex + 1);
    }
    if (newIndex === currentIndex) return;
    setIndex(newIndex);
    const targetScroll = newIndex * step;
    track.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };

  // ===== Render a single carousel =====
  const renderCarousel = (items, type, title, carouselRef, detailsMap, currentIndex, setIndex) => {
    if (items.length === 0) {
      return <div className="empty-message">No trending {type} available</div>;
    }

    const totalItems = items.length;
    const maxIndex = Math.max(0, totalItems - itemsPerView);
    const showLeft = currentIndex > 0;
    const showRight = currentIndex < maxIndex;

    const cardWidth = `calc((100% - (${itemsPerView} - 1) * ${gap}px) / ${itemsPerView})`;
    const trackPaddingLeft = isMobile ? '18px' : '60px';

    return (
      <div className="trending-carousel-wrapper">
        <div className="trending-carousel-header">
          <h2 className="trending-carousel-title">{title}</h2>
        </div>
        <div className="trending-carousel-container">
          {showLeft && (
            <button
              className="carousel-arrow left"
              onClick={() => scrollCarousel(carouselRef, 'left', totalItems, currentIndex, setIndex)}
              aria-label="Scroll left"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
          )}
          <div
            className="trending-carousel-track"
            ref={carouselRef}
            style={{
              gap: `${gap}px`,
              paddingLeft: trackPaddingLeft,
              paddingRight: isMobile ? '20px' : '40px',
            }}
          >
            {items.map((item, index) => {
              const number = index + 1;
              const isLast = number === 15;
              const details = detailsMap[item.id];
              let extraInfo = '';
              if (type === 'movie' && details) {
                const runtime = details.runtime;
                if (runtime) {
                  const hours = Math.floor(runtime / 60);
                  const minutes = runtime % 60;
                  extraInfo = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                } else {
                  extraInfo = 'N/A';
                }
              } else if (type === 'series' && details) {
                const firstYear = details.first_air_date ? details.first_air_date.slice(0,4) : null;
                const lastYear = details.last_air_date ? details.last_air_date.slice(0,4) : null;
                let yearRange = '';
                if (firstYear && lastYear) {
                  yearRange = firstYear === lastYear ? firstYear : `${firstYear}-${lastYear}`;
                } else if (firstYear) {
                  yearRange = firstYear;
                } else {
                  yearRange = 'N/A';
                }
                const seasons = details.number_of_seasons;
                let seasonText = '';
                if (seasons) {
                  if (isMobile && yearRange && yearRange.includes('-')) {
                    seasonText = `${seasons} S.`;
                  } else {
                    seasonText = seasons === 1 ? '1 Season' : `${seasons} Seasons`;
                  }
                } else {
                  seasonText = 'N/A';
                }
                extraInfo = `${yearRange} • ${seasonText}`;
              }

              const inWatchlist = isInWatchlist(item.id, type);

              return (
                <div
                  key={item.id}
                  className={`trending-card ${isLast ? 'last-card' : ''}`}
                  style={{
                    flex: `0 0 ${cardWidth}`,
                    width: cardWidth,
                    scrollSnapAlign: 'start',
                    scrollSnapStop: 'always',
                  }}
                  onClick={() => type === 'movie' ? handleMovieClick(item) : handleSeriesClick(item)}
                >
                  <div className="trending-card-number">{number}</div>
                  <div className="trending-card-poster">
                    <img
                      src={item.poster_path ? `https://image.tmdb.org/t/p/w500${item.poster_path}` : '/placeholder-poster.jpg'}
                      alt={item.title || item.name}
                    />
                    {/* Bookmark button */}
                    <button
                      className={`favorite-btn ${inWatchlist ? 'active' : ''}`}
                      onClick={(e) => toggleWatchlist(e, item, type)}
                      aria-label="Toggle watchlist"
                    >
                      <FontAwesomeIcon icon={inWatchlist ? solidBookmark : regularBookmark} />
                    </button>
                  </div>
                  <div className="trending-card-info">
                    <div className="trending-card-title">{item.title || item.name}</div>
                    <div className="trending-card-meta">
                      {/* Rating first (left) */}
                      {item.vote_average > 0 && (
                        <span className="trending-rating">★ {item.vote_average.toFixed(1)}</span>
                      )}
                      {/* Year and extra info after */}
                      {type === 'movie' && (
                        <>
                          <span>•</span>
                          <span>{item.release_date?.slice(0,4) || item.first_air_date?.slice(0,4) || 'N/A'}</span>
                        </>
                      )}
                      {extraInfo && (
                        <>
                          <span>•</span>
                          <span className="trending-extra-info">{extraInfo}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {showRight && (
            <button
              className="carousel-arrow right"
              onClick={() => scrollCarousel(carouselRef, 'right', totalItems, currentIndex, setIndex)}
              aria-label="Scroll right"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-content" id="trendingPage">
      {(moviesLoading || seriesLoading) && (
        <div className="loading-overlay">
          <img
            src="/loading2.png"
            alt="Loading..."
            className="blinking-logo"
          />
        </div>
      )}

      {!moviesLoading && !seriesLoading && (
        <>
          {renderCarousel(
            trendingMovies,
            'movie',
            t('trendingMovies'),
            moviesCarouselRef,
            movieDetailsMap,
            moviesIndex,
            setMoviesIndex
          )}

          {renderCarousel(
            trendingSeries,
            'series',
            t('trendingSeries'),
            seriesCarouselRef,
            seriesDetailsMap,
            seriesIndex,
            setSeriesIndex
          )}

          {/* Unified Media Modal */}
          {selectedMedia && selectedMediaType && (
            <MediaModal
              media={selectedMedia}
              mediaType={selectedMediaType}
              onClose={closeModal}
              onMediaChange={handleMediaChange}
            />
          )}
        </>
      )}

      {/* ===== STYLES ===== */}
      <style jsx>{`
        .carousel-arrow {
          position: absolute;
          top: 40%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: white;
          font-size: 32px;
          cursor: pointer;
          transition: all 0.3s ease;
          filter: drop-shadow(0 0 12px rgba(0, 0, 0, 0.9));
          text-shadow: 0 0 10px rgba(0, 0, 0, 0.8);
          padding-top: 60px;
          padding-bottom: 60px;
          opacity: 0.7;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          background: rgba(0, 0, 0, 0);
          box-shadow: 0 0 15px rgba(0, 0, 0, 0);
        }

        .carousel-arrow:hover {
          opacity: 1;
          filter: drop-shadow(0 0 16px rgba(0, 0, 0, 0.95));
          text-shadow: 0 0 14px rgba(0, 0, 0, 0.9);
        }

        .carousel-arrow.left {
          left: -30px;
        }

        .carousel-arrow.right {
          right: -30px;
        }

        @media (max-width: 1200px) {
          .carousel-arrow {
            width: 40px;
            height: 40px;
            font-size: 24px;
            padding-top: 40px;
            padding-bottom: 40px;
            display: none;
          }
          .carousel-arrow.left {
            left: -20px;
          }
          .carousel-arrow.right {
            right: -20px;
          }
        }

        @media (min-width: 1200px) {
          .trending-card-number {
            left: -45px !important;
          }
        }

        /* ===== POSTER SCALING ===== */
        .trending-card-poster img {
          transition: transform 0.3s ease;
          transform: scale(1);
        }

        /* On card hover: scale to 1.10 */
        .trending-card:hover .trending-card-poster img {
          transform: scale(1.05);
        }

        /* On bookmark hover: reset to 1.00 (overrides card hover) */
        .trending-card-poster:has(.favorite-btn:hover) img {
          transform: scale(1.00) !important;
        }

        /* ===== BOOKMARK VISIBILITY ===== */
        .trending-card .favorite-btn {
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
          display: flex !important;
          width: 32px;
          height: 32px;
          font-size: 18px;
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(0,0,0,0.5);
          border: none;
          border-radius: 50%;
          color: white;
          cursor: pointer;
          z-index: 5;
          backdrop-filter: blur(4px);
          align-items: center;
          justify-content: center;
          padding: 0;
        }

        .trending-card:hover .favorite-btn {
          opacity: 1;
          pointer-events: auto;
        }

        /* ===== MOBILE ===== */
        @media (max-width: 768px) {
          .trending-card .favorite-btn {
            opacity: 1 !important;
            pointer-events: auto !important;
            width: 25px !important;
            height: 25px !important;
            font-size: 14px !important;
            top: 5px !important;
            right: 5px !important;
          }

          /* Keep meta on one line */
          .trending-card-meta {
            flex-wrap: nowrap !important;
            gap: 4px !important;
            font-size: 11px !important;
          }

          .trending-card-title {
            font-size: 12px !important;
          }

          .trending-extra-info {
            font-size: 10px !important;
          }

          .trending-rating {
            font-size: 10px !important;
          }

          /* Number positions – adjusted for 18px left padding */
          .trending-card-number {
            left: -18px !important;
          }
          .trending-card.last-card .trending-card-number {
            left: -22px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default TrendingPage;